import { useState } from 'react'
import DocLayout from '../../components/docs/DocLayout'
import Icon from '../../components/docs/Icon'
import { tokens } from '@/content/system'
import Cased from '@/components/ui/Cased'

function parseBezier(value: string): [number, number, number, number] | null {
  const match = value.match(/cubic-bezier\(([^)]+)\)/)
  if (!match) return null
  const parts = match[1].split(',').map((part) => Number(part.trim()))
  return parts.length === 4 ? (parts as [number, number, number, number]) : null
}

function Curve({ value }: { value: string }) {
  const points = parseBezier(value)
  if (!points) return null
  const [x1, y1, x2, y2] = points
  const size = 100
  const pad = 8
  const map = (x: number, y: number) => `${pad + x * (size - pad * 2)} ${size - pad - y * (size - pad * 2)}`
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="fnd-curve" aria-hidden="true">
      <rect x={pad} y={pad} width={size - pad * 2} height={size - pad * 2} className="fnd-curve__frame" />
      <line x1={pad} y1={size - pad} x2={pad + x1 * (size - pad * 2)} y2={size - pad - y1 * (size - pad * 2)} className="fnd-curve__handle" />
      <line x1={size - pad} y1={pad} x2={pad + x2 * (size - pad * 2)} y2={size - pad - y2 * (size - pad * 2)} className="fnd-curve__handle" />
      <path d={`M${map(0, 0)} C${map(x1, y1)} ${map(x2, y2)} ${map(1, 1)}`} className="fnd-curve__path" />
    </svg>
  )
}

export default function MotionPage() {
  const [run, setRun] = useState(0)
  const easings = tokens.filter((token) => token.css.startsWith('--mds-ease-'))
  const durations = tokens.filter((token) => token.css.startsWith('--mds-duration-'))

  return (
    <DocLayout section={{ label: 'Foundations', to: '/foundations' }}
      eyebrow="Foundations"
      title="Motion"
      intro={
        <p>
          Motion explains a change: where a sheet came from, what just updated, which step is next. It stays quick and
          quiet, and it stops entirely for visitors who ask their device for reduced motion.
        </p>
      }
      wide
    >
      <section className="doc-section" aria-labelledby="motion-easing">
        <h2 id="motion-easing"><Cased>Easing</Cased></h2>
        <p>Three curves cover every transition. Each graph plots progress against time.</p>
        <div className="fnd-easings" key={run}>
          {easings.map((token) => (
            <article key={token.css} className="fnd-easing">
              <Curve value={token.value} />
              <div className="fnd-easing__demo" aria-hidden="true">
                <span className="fnd-easing__dot" style={{ animationTimingFunction: `var(${token.css})` }} />
              </div>
              <code>{token.css}</code>
              <span className="fnd-easing__value">{token.value}</span>
              <span className="mds-muted">{token.role}</span>
            </article>
          ))}
        </div>
        <p>
          <button type="button" className="mds-button mds-button--secondary mds-button--sm" onClick={() => setRun((n) => n + 1)}>
            <Icon name="play" size={14} /> Replay
          </button>
        </p>
      </section>

      <section className="doc-section" aria-labelledby="motion-duration">
        <h2 id="motion-duration"><Cased>Duration</Cased></h2>
        <p>Short for small things, longer for large ones. Nothing a person waits on takes longer than story motion.</p>
        <div className="doc-table-wrap" tabIndex={0}>
          <table className="doc-table">
            <thead>
              <tr>
                <th scope="col">Token</th>
                <th scope="col">Value</th>
                <th scope="col">Use</th>
                <th scope="col">
                  <span className="mds-visually-hidden">Demonstration</span>
                </th>
              </tr>
            </thead>
            <tbody key={run}>
              {durations.map((token) => (
                <tr key={token.css}>
                  <th scope="row">
                    <code>{token.css}</code>
                  </th>
                  <td>
                    <code>{token.value}</code>
                  </td>
                  <td className="mds-muted">{token.role}</td>
                  <td>
                    <span className="fnd-duration" aria-hidden="true">
                      <span style={{ animationDuration: `var(${token.css})` }} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="motion-reduced">
        <h2 id="motion-reduced"><Cased>Reduced motion</Cased></h2>
        <p>
          When a device reports <code>prefers-reduced-motion: reduce</code>, MDS shortens every transition and animation to
          an instant. Pinned scroll stories become plain scrolling, parallax stops, the waving flag and the turning star
          ring stand still, and background photographs stop crossfading.
        </p>
        <pre className="mds-code" tabIndex={0}>
          <code>{`@media (prefers-reduced-motion: reduce) {\n  *, *::before, *::after {\n    animation-duration: 0.01ms !important;\n    animation-iteration-count: 1 !important;\n    transition-duration: 0.01ms !important;\n    scroll-behavior: auto !important;\n  }\n}`}</code>
        </pre>
        <ul>
          <li>Anything that moves for more than five seconds has a pause control, as WCAG 2.2.2 requires.</li>
          <li>Nothing flashes more than three times a second.</li>
          <li>Motion never carries meaning alone; the end state says what happened.</li>
        </ul>
        <p>
          Source: <a href="https://www.w3.org/WAI/WCAG21/Understanding/pause-stop-hide.html">Understanding WCAG 2.2.2 Pause, Stop, Hide</a>.
        </p>
      </section>
    </DocLayout>
  )
}
